import { create } from "zustand";
import { useModelStore, type ModelType } from "./modelStore";

export type SubscriptionTier = "storyteller" | "advanced" | "ultimate" | null;

type UserData = {
  is_premium?: boolean;
  subscription_tier?: SubscriptionTier;
};

type MembershipState = {
  isPremium: boolean;
  subscriptionTier: SubscriptionTier;
  setIsPremium: (value: boolean) => void;
  setSubscriptionTier: (value: SubscriptionTier) => void;
  syncFromUserData: (userData: UserData) => void;
  fetchAndSync: () => Promise<void>;
  isAdvancedOrHigher: () => boolean;
};

const PREMIUM_ONLY_MODELS: ModelType[] = ["chatgpt", "deepseek", "mistral"];
const ADVANCED_ONLY_MODELS: ModelType[] = [
  "claude",
  "wizardlm",
  "hermes",
  "llama8b",
  "lunaris",
  "llama70b",
  "gemini",
  "glm",
];

function modelIsAllowedForTier(
  model: ModelType,
  isPremium: boolean,
  subscriptionTier: SubscriptionTier,
): boolean {
  if (PREMIUM_ONLY_MODELS.includes(model) && !isPremium) {
    return false;
  }

  const isAdvancedOrHigher =
    subscriptionTier === "advanced" || subscriptionTier === "ultimate";
  if (ADVANCED_ONLY_MODELS.includes(model) && !isAdvancedOrHigher) {
    return false;
  }

  return true;
}

const useMembershipStore = create<MembershipState>((set, get) => ({
  isPremium: false,
  subscriptionTier: null,
  setIsPremium: (value: boolean) => set({ isPremium: value }),
  setSubscriptionTier: (value: SubscriptionTier) =>
    set({ subscriptionTier: value }),
  syncFromUserData: (userData: UserData) => {
    const resolved = userData.is_premium ?? false;
    const tier = userData.subscription_tier ?? null;
    console.log("[membershipStore.syncFromUserData] incoming", {
      is_premium: userData.is_premium,
      subscription_tier: userData.subscription_tier,
      resolved,
      tier,
    });
    set({ isPremium: resolved, subscriptionTier: tier });

    const currentModel = useModelStore.getState().selectedModel;
    if (!modelIsAllowedForTier(currentModel, resolved, tier)) {
      useModelStore.getState().setSelectedModel("recommended");
    }
  },
  isAdvancedOrHigher: () => {
    const tier = get().subscriptionTier;
    return tier === "advanced" || tier === "ultimate";
  },
  fetchAndSync: async () => {
    try {
      const response = await fetch("/api/current-user", {
        credentials: "include",
      });
      if (!response.ok) {
        console.log("membershipStore.fetchAndSync: non-OK response", {
          status: response.status,
          statusText: response.statusText,
        });
        // Keep prior state on error to avoid false negatives in UI
        return;
      }
      const userData = await response.json();
      const resolved = userData.is_premium ?? false;
      const tier = userData.subscription_tier ?? null;
      console.log("[membershipStore.fetchAndSync] success", {
        status: response.status,
        is_premium: userData.is_premium,
        subscription_tier: userData.subscription_tier,
        resolved,
        tier,
      });
      set({ isPremium: resolved, subscriptionTier: tier });

      const currentModel = useModelStore.getState().selectedModel;
      if (!modelIsAllowedForTier(currentModel, resolved, tier)) {
        useModelStore.getState().setSelectedModel("recommended");
      }
    } catch (error) {
      console.error("Failed to fetch user data for membership store:", error);
      // Keep prior state on error to avoid false negatives in UI
    }
  },
}));

export default useMembershipStore;
